import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import { API_URL } from "../../config";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useParams, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faChevronLeft, faChevronRight, faSearch } from "@fortawesome/free-solid-svg-icons";

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, Title);

const defaultColors = ["#F59E0B", "#22C55E"]; // Pending/Error vs Success

const chartOptions = {
  responsive: true,
  plugins: {
    legend: {
      position: "bottom",
      labels: {
        font: {
          size: 12,
          family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        },
        color: "#3D5A80",
        padding: 10,
        usePointStyle: true,
        pointStyle: "circle",
      },
    },
    tooltip: {
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      titleColor: "#293241",
      bodyColor: "#3D5A80",
      borderColor: "#22C55E",
      borderWidth: 1,
      cornerRadius: 6,
      titleFont: { size: 12, weight: "bold" },
      bodyFont: { size: 11 },
      padding: 8,
    },
  },
  animation: {
    animateScale: true,
    animateRotate: true,
  },
  maintainAspectRatio: false,
};

const BroadcastDetails = () => {
  const { cid } = useParams();
  const navigate = useNavigate();

  const [chartData, setChartData] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filters & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const limit = 50;

  // Fetch Chart Data
  useEffect(() => {
    const fetchChartData = async () => {
      if (!cid) {
        setError("No Campaign ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem("authToken");

        if (!token) {
          setError("Authentication token not found");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${API_URL}broadcast/chart?cid=${cid}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              accept: "*/*",
            },
            timeout: 30000,
          }
        );

        if (response.data && response.data.status && response.data.data) {
          setChartData(response.data.data);
        } else {
          setError("Invalid chart data format received from server");
        }
      } catch (err) {
        console.error("Error fetching broadcast chart:", err);
        setError(err.response?.data?.message || "Failed to load broadcast chart data");
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [cid]);

  // Fetch Recipient Table Data
  const fetchTableData = async () => {
    if (!cid) return;
    setTableLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const offset = (page - 1) * limit;
      let url = `${API_URL}broadcast/whatsapp?cid=${cid}&orderBy=created_on&limit=${limit}&offset=${offset}`;
      
      if (searchQuery) {
        url += `&recipient=${encodeURIComponent(searchQuery)}`;
      }

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          accept: "*/*",
        },
        timeout: 30000,
      });

      if (response.data && response.data.status) {
        setTableData(response.data.data || []);
        setTotalCount(response.data.total || 0);
      } else {
        setTableData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching broadcast table details:", err);
      setTableData([]);
      setTotalCount(0);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [cid, page, searchQuery]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setSearchQuery(searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setSearchQuery("");
    setPage(1);
  };

  const pageCount = Math.ceil(totalCount / limit) || 1;

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  // Prepare chart representation
  const pieChartConfig = useMemo(() => {
    if (!chartData || !chartData.whatsapp) {
      return {
        labels: ["No Data"],
        datasets: [
          {
            data: [1],
            backgroundColor: ["#CCCCCC"],
          },
        ],
      };
    }

    const whatsappData = chartData.whatsapp;
    return {
      labels: whatsappData.map((item) => item.label),
      datasets: [
        {
          label: "WhatsApp Distribution",
          data: whatsappData.map((item) => item.value),
          backgroundColor: whatsappData.map(
            (item, idx) => item.color || defaultColors[idx] || "#CCCCCC"
          ),
          borderColor: ["#fff"],
          borderWidth: 2,
          hoverBorderColor: "#000",
          hoverBorderWidth: 2,
        },
      ],
    };
  }, [chartData]);

  if (loading) {
    return (
      <div className="container-fluid" style={{ marginTop: "30px", textAlign: "center" }}>
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid" style={{ marginTop: "30px" }}>
        <button className="btn btn-default mb-3" onClick={() => navigate("/Report/Broadcast")}>
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" /> Back to Broadcast Report
        </button>
        <div className="panel panel-danger text-center p-5 bg-white rounded shadow-sm border">
          <h3 className="text-danger">Error Loading Broadcast Details</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid" style={{ marginTop: "20px" }}>
      {/* Page Header / Back Button */}
      <div className="d-flex align-items-center justify-content-between mb-4">
        <button
          className="btn btn-default btn-sm shadow-sm"
          onClick={() => navigate("/Report/Broadcast")}
          style={{ border: "1px solid #ccc", padding: "6px 12px", background: "#fff" }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </button>
        <h3 style={{ margin: 0, fontWeight: "600", color: "#2c3e50" }}>
          Broadcast Campaign Details (ID: {cid})
        </h3>
        <div style={{ width: "70px" }}></div>
      </div>

      <div className="row">
        {/* Chart Column */}
        <div className="col-md-4 mb-4">
          <div
            className="panel panel-default shadow-sm border bg-white"
            style={{
              borderRadius: "8px",
              padding: "20px",
              minHeight: "360px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <h5 className="text-center mb-3" style={{ fontWeight: "600", color: "#34495e" }}>
              Delivery Distribution
            </h5>
            <div style={{ width: "100%", height: "250px", position: "relative" }}>
              <Pie data={pieChartConfig} options={chartOptions} />
            </div>
          </div>
        </div>

        {/* Recipients Table Column */}
        <div className="col-md-8 mb-4">
          <div
            className="panel panel-default shadow-sm border bg-white"
            style={{ borderRadius: "8px", padding: "20px", minHeight: "360px" }}
          >
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-3 gap-2">
              <h5 style={{ margin: 0, fontWeight: "600", color: "#34495e" }}>
                WhatsApp Recipients ({totalCount})
              </h5>
              
              {/* Search Form */}
              <form onSubmit={handleSearchSubmit} className="d-flex gap-2 w-100 w-md-auto" style={{ maxWidth: "300px" }}>
                <div className="input-group input-group-sm">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search recipient..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchQuery && (
                    <button type="button" className="btn btn-default btn-sm border-start-0" onClick={handleClearSearch}>
                      &times;
                    </button>
                  )}
                  <button type="submit" className="btn btn-primary btn-sm">
                    <FontAwesomeIcon icon={faSearch} />
                  </button>
                </div>
              </form>
            </div>

            <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
              <table className="table table-striped table-bordered table-hover align-middle mb-0 bg-white">
                <thead className="table-light" style={{ position: "sticky", top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Recipient</th>
                    <th style={{ width: "130px", textAlign: "center" }}>Status</th>
                    <th>Sent Time</th>
                    <th>Message</th>
                    <th>Message ID</th>
                  </tr>
                </thead>
                <tbody>
                  {tableLoading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index} className="skeleton-row">
                        {Array.from({ length: 5 }).map((_, colIndex) => (
                          <td key={colIndex}>
                            <div className="skeleton-box" style={{ height: "16px", background: "#f2f2f2", borderRadius: "4px" }}></div>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : tableData.length > 0 ? (
                    tableData.map((item, index) => (
                      <tr key={item.id || index}>
                        <td style={{ fontWeight: "500" }}>{item.recipient || "-"}</td>
                        <td style={{ textAlign: "center" }}>
                          <span
                            className={`badge ${
                              item.status === "Success" ? "bg-success" : "bg-warning text-dark"
                            }`}
                            style={{ padding: "5px 10px", fontSize: "11px", borderRadius: "4px" }}
                          >
                            {item.status}
                          </span>
                        </td>
                        <td style={{ whiteSpace: "nowrap" }}>{item.created_on || "-"}</td>
                        <td style={{ maxWidth: "250px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={item.message}>
                          {item.message || "-"}
                        </td>
                        <td>{item.messageid || "-"}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="text-center py-4 text-muted">
                        No records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {pageCount > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <span className="text-muted" style={{ fontSize: "13px" }}>
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, totalCount)} of {totalCount}
                </span>
                <ul className="pagination pagination-sm mb-0">
                  <li className={`page-item ${page === 1 ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(page - 1)}>
                      <FontAwesomeIcon icon={faChevronLeft} />
                    </button>
                  </li>
                  <li className="page-item active">
                    <span className="page-link">{page}</span>
                  </li>
                  <li className={`page-item ${page === pageCount ? "disabled" : ""}`}>
                    <button className="page-link" onClick={() => handlePageChange(page + 1)}>
                      <FontAwesomeIcon icon={faChevronRight} />
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BroadcastDetails;
